<template>
  <BaseModal
    v-if="nextText"
    class="logo-modal"
    color="grey"
    rounded
    size="lg"
    @background-clicked="$emit('close')"
  >
    <h1 class="logo-modal__title">Add Your Company Logo to Your Report</h1>
    <div class="logo-modal__content-container">
      <template v-if="step === 'selectImage'">
        <div class="logo-modal__description-container">
          <h4>Upload your company logo.</h4>
          <p>Image requirements:</p>
          <ul class="logo-modal__description-list">
            <li class="logo-modal__description-list-item">PNG file only</li>
            <li class="logo-modal__description-list-item">2MB file limit</li>
          </ul>
        </div>
        <LogoDropfile
          :selected-file="selectedFile"
          :gallery-count="galleryCount"
          class="logo-modal__action-container"
          @file-uploaded="handleFileUpload($event)"
          @select-from-gallery="step = 'imageGallery'"
        />
      </template>
      <template v-else-if="step === 'imageGallery'">
        <div class="logo-modal__description-container">
          <h4>Upload your company logo.</h4>
          <p>Image requirements:</p>
          <ul class="logo-modal__description-list">
            <li class="logo-modal__description-list-item">PNG file only</li>
            <li class="logo-modal__description-list-item">2MB file limit</li>
          </ul>
        </div>
        <LogoGallery
          :currently-selected-image="selectedImage"
          :report-type-id="reportTypeId"
          :currentReportImage="currentLogo"
          class="logo-modal__action-container"
          @selected-image="selectedImage = $event"
          @empty-images="step = 'selectImage'"
        />
      </template>
      <template v-else-if="step === 'cropImage'">
        <LogoCropper
          v-if="selectedFile"
          :image="selectedFile"
          @file-cropped="handleCroppedFile"
        />
      </template>
      <div id="captureDiv" style="position: absolute; z-index: -100">
        <div
          style="
            width: 500px;
            height: 300px;
            display: flex;
            background-color: transparent;
            justify-content: center;
            align-items: center;
          "
        >
          <img
            :src="virtualImageSrc"
            style="max-width: 100%; max-height: 100%"
          />
        </div>
      </div>
    </div>
    <div>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </div>
    <div class="logo-modal__navigation">
      <BaseButton
        :disabled="isNotValid"
        class="logo-modal__navigation-button"
        @click="navigateNext()"
      >
        {{ nextText }}
      </BaseButton>
      <BaseButton
        class="logo-modal__navigation-button"
        color="charcoal"
        @click="navigateBack()"
      >
        {{ backText }}
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script>
  import { useConsultingStore } from '~/stores/consulting';
  import BaseModal from '~/components/base/BaseModal.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import LogoDropfile from '~/components/logo/LogoDropfile.vue';
  import LogoGallery from './LogoGallery.vue';
  import LogoCropper from './LogoCropper.vue';
  import html2canvas from 'html2canvas';

  export default {
    name: 'LogoModal',
    components: {
      BaseModal,
      BaseButton,
      LogoDropfile,
      LogoGallery,
      LogoCropper,
    },
    props: {
      reportTypeId: {
        type: [Number, String],
        required: true,
      },
    },
    data: () => ({
      selectedFile: null,
      selectedImage: null,
      croppedImage: null,
      virtualImageSrc: null,
      errorMessage: '',
      step: 'selectImage',
      stepMap: {
        selectImage: {
          previous: null,
          previousText: 'GO BACK',
          next: 'cropImage',
          nextText: 'NEXT',
        },
        cropImage: {
          previous: 'selectImage',
          previousText: 'GO BACK',
          next: 'uploadImage',
          nextText: 'UPLOAD LOGO',
        },
        imageGallery: {
          previous: 'selectImage',
          previousText: 'GO BACK',
          next: 'setLogo',
          nextText: 'NEXT',
        },
        uploadImage: {
          previous: 'cropImage',
          previousText: 'GO BACK',
          next: null,
          nextText: null,
        },
        setLogo: {
          previous: 'imageGallery',
          previousText: null,
          next: null,
          nextText: null,
        },
      },
    }),
    setup() {
      const consultingStore = useConsultingStore();
      return { consultingStore };
    },
    computed: {
      nextStep() {
        return this.stepMap[this.step].next;
      },
      previousStep() {
        return this.stepMap[this.step].previous;
      },
      nextText() {
        return this.stepMap[this.step].nextText;
      },
      backText() {
        return this.stepMap[this.step].previousText;
      },
      isNaviagteNextDisabled() {
        if (this.step === 'selectImage' && !this.selectedFile) {
          return true;
        } else if (this.step === 'cropImage' && !this.croppedImage) {
          return true;
        } else if (this.step === 'uploadImage' && !this.selectedFile) {
          return true;
        }

        return false;
      },
      isNotValid() {
        if (!this.selectedFile && !this.selectedImage) return true;
        if (this.selectedFile && this.selectedFile.type !== 'image/png')
          return true;
        if (this.selectedFile && this.selectedFile.size > 2000000) return true;
        return false;
      },
      galleryCount() {
        return this.consultingStore.getGalleryCount;
      },
      currentLogo() {
        return this.consultingStore.getCurrentLogo;
      },
    },
    async mounted() {
      this.selectedImage = this.consultingStore.getCurrentLogo;
      await this.consultingStore.setGalleryCount(this.reportTypeId);
    },
    methods: {
      async handleFileUpload(event) {
        if (event.type !== 'image/png' || event.size > 2000000) {
          this.selectedFile = event;
          return;
        }
        const image = new Image();
        image.src = URL.createObjectURL(event);

        await new Promise((resolve) => {
          image.onload = () => {
            resolve();
          };
        });

        this.virtualImageSrc = image.src;
        await this.$nextTick();

        const elementToCapture = document.getElementById('captureDiv');

        const canvas = await html2canvas(elementToCapture, {
          allowTaint: true,
          backgroundColor: 'transparent',
        });

        const imgDataUrl = canvas.toDataURL('image/png');
        const blob = await fetch(imgDataUrl).then((response) =>
          response.blob()
        );
        const file = new File([blob], 'image.png', { type: 'image/png' });

        this.selectedFile = file;
      },
      async uploadImage() {
        try {
          const submitForm = new FormData();
          submitForm.append('image', this.croppedImage);
          submitForm.append('reportTypeId', this.reportTypeId);

          const { data } = await axios.post(`/company-logo`, submitForm);
          if (this.consultingStore.getReportTypeLogo) {
            this.updateReportTypeLogo(data);
          } else {
            this.createReportTypeLogo(data);
          }
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      async updateReportTypeLogo(image) {
        this.consultingStore.updateReportTypeLogo(image, this.reportTypeId);
        this.$emit('new-image-selected', {
          reportTypeLogo: this.consultingStore.getReportTypeLogo,
          companyLogo: image,
          close: false,
        });
      },
      async createReportTypeLogo(image) {
        this.consultingStore.createReportTypeLogo(image, this.reportTypeId);
        this.$emit('new-image-selected', {
          reportTypeLogo: this.consultingStore.getReportTypeLogo,
          companyLogo: image,
          close: false,
        });
        this.step = 'imageGallery';
      },
      handleCroppedFile(file) {
        this.croppedImage = file;
      },
      navigateNext() {
        if (this.step === 'selectImage' && !this.selectedFile) {
          this.errorMessage = 'Please select an image.';
          return;
        } else if (this.step === 'cropImage' && !this.croppedImage) {
          this.errorMessage = 'Please crop your image.';
          return;
        } else if (this.step === 'uploadImage' && !this.selectedFile) {
          this.errorMessage = 'Please upload an image.';
          return;
        }

        this.errorMessage = '';
        this.step = this.nextStep;

        if (this.step === 'uploadImage' && this.croppedImage) {
          this.uploadImage(this.croppedImage);
        } else if (this.step === 'setLogo' && this.selectedImage) {
          if (this.consultingStore.getReportTypeLogo) {
            this.consultingStore.updateReportTypeLogo(
              this.selectedImage,
              this.reportTypeId
            );
          } else {
            this.consultingStore.createReportTypeLogo(
              this.selectedImage,
              this.reportTypeId
            );
          }
        }
      },
      navigateBack() {
        if (!this.previousStep) {
          this.$emit('close');
          return;
        }
        this.step = this.previousStep;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .logo-modal {
    &__title {
      font-size: 30px;
      font-weight: 700;
      margin-bottom: 3rem;
      text-align: center;
    }
    &__content-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 1rem;
    }
    &__action-container {
      flex: 1 auto;
      padding: 10px;
      background: darken(#dbdcde, 5%);
    }
    &__description-container {
      min-width: 30%;
      h4,
      p {
        color: var(--itr-charcoal);
      }
      h4 {
        margin-bottom: 3rem;
      }
    }
    &__description-list {
      margin-top: 0 !important;
      list-style: disc;
      margin-left: 32px;
      color: var(--itr-charcoal);
    }
    &__description-list-item {
      padding: 0 0 5px 5px;
      &::before {
        content: '' !important;
        display: none !important;
      }
    }
    &__cropper-container {
      position: relative;
      margin-top: 1rem;
      width: 100%;
      overflow: hidden;
    }
    &__navigation {
      display: flex;
      justify-content: center;
      gap: 2.5rem;
      padding: 1rem 0;
    }
    &__navigation-button {
      width: 100%;
      max-width: 250px;
    }
  }
</style>
